import { useState, useEffect, useCallback } from "react";
import {
  Search, ChevronDown, ChevronRight, X, FileText, Package,
  Award, CheckCircle, XCircle, RefreshCw,
  Star, LayoutGrid, List, Calendar, Trophy, Zap,
  BarChart2, ShoppingCart, AlertTriangle,
  MessageCircle, Trash2
} from "lucide-react";
import API_BASE_URL from "../config";
import ActivityFeedModal from "../Activity/Activityfeedmodal_Quotation";
import Swal from "sweetalert2";

// ─── Helpers ────────────────────────────────────────────────────
const fmt = (v) =>
  isNaN(v) || v === "" || v === null || v === undefined
    ? "—"
    : `₱${Number(v).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-PH", {
    year: "numeric", month: "short", day: "2-digit",
  });
};

const isInRange = (dateStr, range) => {
  if (!dateStr || range === "all") return true;
  const d = new Date(dateStr);
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (range === "today") return d >= startOfDay;
  if (range === "week") {
    const w = new Date(startOfDay);
    w.setDate(startOfDay.getDate() - startOfDay.getDay());
    return d >= w;
  }
  if (range === "month") {
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }
  return true;
};

// ─── Status ─────────────────────────────────────────────────────
const STATUS_MAP = {
  0: { label: "Draft", bg: "bg-slate-100", text: "text-slate-600", border: "border-slate-300" },
  1: { label: "Submitted", bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  2: { label: "Awarded", bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
  3: { label: "Rejected", bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] ?? STATUS_MAP[0];
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${s.bg} ${s.text} ${s.border}`}>
      {s.label}
    </span>
  );
}

function StarDisplay({ value = 0, size = 13 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} size={size}
          className={s <= value ? "fill-amber-400 text-amber-400" : "fill-slate-100 text-slate-300"} />
      ))}
    </div>
  );
}

// ─── Confirmation Modal ──────────────────────────────────────────
function ConfirmSelectModal({ quotation, onConfirm, onCancel, loading }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl mx-4 overflow-hidden">
        <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
            <CheckCircle size={20} className="text-blue-600" />
          </div>
          <div>
            <h2 className="text-slate-800 font-bold text-lg leading-tight">Confirm Selection</h2>
            <p className="text-slate-400 text-sm mt-0.5">Are you sure you want to select this quotation?</p>
          </div>
        </div>

        <div className="px-6 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Supplier</span>
              <span className="text-slate-800 font-semibold">{quotation.supplierName}</span>
            </div>
            <div className="flex justify-between text-sm items-center">
              <span className="text-slate-500">Quotation No.</span>
              <span className="font-mono text-blue-700 font-semibold text-xs bg-blue-50 border border-blue-100 px-2 py-0.5 rounded">{quotation.quotationNo}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">PR #</span>
              <span className="text-slate-700 font-semibold">#{quotation.prHeaderId}</span>
            </div>
            {quotation.deliveryDays && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Delivery</span>
                <span className="text-slate-700">{quotation.deliveryDays} days</span>
              </div>
            )}
            {quotation.paymentTerms && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Payment Terms</span>
                <span className="text-slate-700">{quotation.paymentTerms}</span>
              </div>
            )}
          </div>

          {(quotation.quotationDetails || []).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Package size={12} className="text-slate-400" /> Items
              </p>
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="text-left py-2 px-3 text-slate-400 font-semibold">#</th>
                      <th className="text-left py-2 px-3 text-slate-400 font-semibold">Item</th>
                      <th className="text-right py-2 px-3 text-slate-400 font-semibold whitespace-nowrap">Qty</th>
                      <th className="text-right py-2 px-3 text-slate-400 font-semibold whitespace-nowrap">Unit Price</th>
                      <th className="text-right py-2 px-3 text-slate-400 font-semibold whitespace-nowrap">Disc</th>
                      <th className="text-right py-2 px-3 text-slate-400 font-semibold whitespace-nowrap">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(quotation.quotationDetails || []).map((item, idx) => {
                      const get = (obj, ...keys) => {
                        for (const k of keys) {
                          if (obj[k] !== undefined && obj[k] !== null && obj[k] !== "") return obj[k];
                        }
                        return null;
                      };
                      const unitPrice = parseFloat(get(item, "unitPrice", "UnitPrice", "unit_price")) || 0;
                      const discPct = parseFloat(get(item, "discountPercent", "DiscountPercent", "discount_percent")) || 0;
                      const discAmt = parseFloat(get(item, "discountAmount", "DiscountAmount", "discount_amount")) || 0;
                      const totalAmount = parseFloat(get(item, "totalAmount", "TotalAmount", "total_amount")) || 0;
                      const qty = parseFloat(get(item, "qty", "Qty", "QTY")) || 0;
                      const uom = get(item, "uom", "UOM", "Uom", "uOM") || "";
                      const itemDesc = get(item, "itemDescription", "ItemDescription", "itemDesciption") || "—";
                      const itemCode = get(item, "itemCode", "ItemCode") || "";
                      const discountedUnit = discPct > 0 ? unitPrice * (1 - discPct / 100) : null;

                      return (
                        <tr key={idx} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                          <td className="py-2.5 px-3 text-slate-400">{idx + 1}</td>
                          <td className="py-2.5 px-3">
                            <div className="font-semibold text-slate-700 leading-tight" title={itemDesc}>{itemDesc}</div>
                            {itemCode && (
                              <span className="font-mono text-blue-600 text-[10px] bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded mt-0.5 inline-block">
                                {itemCode}
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-right text-slate-600 font-semibold whitespace-nowrap">
                            {qty.toLocaleString()}
                            {uom && <span className="text-slate-400 font-normal ml-1">{uom}</span>}
                          </td>
                          <td className="py-2.5 px-3 text-right whitespace-nowrap">
                            {discPct > 0 ? (
                              <div className="flex flex-col items-end">
                                <span className="text-slate-400 line-through text-[10px]">{fmt(unitPrice)}</span>
                                <span className="text-slate-700 font-semibold">{fmt(discountedUnit)}</span>
                              </div>
                            ) : (
                              <span className="text-slate-700 font-semibold">{fmt(unitPrice)}</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-right whitespace-nowrap">
                            {discPct > 0 ? (
                              <div className="flex flex-col items-end gap-0.5">
                                <span className="inline-flex items-center bg-amber-50 border border-amber-200 text-amber-600 font-bold px-2 py-0.5 rounded-full text-[10px]">
                                  -{discPct}%
                                </span>
                                {discAmt > 0 && <span className="text-amber-500 text-[10px]">-{fmt(discAmt)}</span>}
                              </div>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-right text-green-600 font-bold whitespace-nowrap">
                            {fmt(totalAmount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-200 bg-slate-50">
                      <td colSpan={5} className="py-2.5 px-3 text-right text-slate-500 font-semibold">Grand Total</td>
                      <td className="py-2.5 px-3 text-right text-green-600 font-black text-sm">{fmt(quotation.grandTotal)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-amber-700 text-xs leading-relaxed">
              This quotation will be marked as selected for PR #{quotation.prHeaderId}.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 justify-end">
          <button onClick={onCancel} disabled={loading}
            className="px-5 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all disabled:opacity-50">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="px-5 py-2.5 text-sm font-bold text-white bg-blue-600 border border-blue-700 rounded-xl hover:bg-blue-700 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-blue-200">
            {loading ? <><RefreshCw size={14} className="animate-spin" /> Selecting...</> : <><CheckCircle size={14} /> Yes, Select</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Best Quotation Comparison View ─────────────────────────────
function BestQuotationView({ prGroup, onSelectSuccess, onOpenFeed, userRole, onDelete }) {
  const { prHeaderId, quotations, prDetails } = prGroup;
  const [expanded, setExpanded] = useState(false);
  const [selecting, setSelecting] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingTarget, setRatingTarget] = useState(null);
  const [savingRating, setSavingRating] = useState(false);
  const [localQuotations, setLocalQuotations] = useState(() =>
    quotations.map(q => ({
      ...q,
      quotationDetails: q.quotationDetails || [],
      selected: q.selected ?? q.Selected ?? q.isSelected ?? false,
    }))
  );

  useEffect(() => {
    setLocalQuotations(quotations.map(q => ({
      ...q,
      quotationDetails: q.quotationDetails || [],
      selected: q.selected ?? q.Selected ?? q.isSelected ?? false,
    })));
  }, [quotations]);

  const normalizeKey = (code, desc) => {
    const c = (code || "").trim().toLowerCase();
    const d = (desc || "").trim().toLowerCase();
    return c || d;
  };

  const buildItemMatrix = () => {
    const itemMap = new Map();
    (prDetails || []).forEach((prItem) => {
      const key = normalizeKey(prItem.itemCode, prItem.itemDescription || prItem.itemDesciption);
      if (!key) return;
      if (!itemMap.has(key)) {
        itemMap.set(key, {
          key,
          itemCode: prItem.itemCode || "—",
          itemDescription: prItem.itemDescription || prItem.itemDesciption || "—",
          specification: prItem.materialFinish || prItem.specification || "—",
          originalQty: prItem.qty,
          originalUOM: prItem.uom,
          supplierPrices: {},
        });
      }
    });

    localQuotations.forEach((q) => {
      (q.quotationDetails || []).forEach((detail) => {
        const key = normalizeKey(detail.itemCode, detail.itemDescription);
        if (!key) return;
        if (!itemMap.has(key)) {
          itemMap.set(key, {
            key,
            itemCode: detail.itemCode || "—",
            itemDescription: detail.itemDescription || "—",
            specification: detail.specification || "—",
            originalQty: detail.qty,
            originalUOM: detail.uom,
            supplierPrices: {},
          });
        }
        const item = itemMap.get(key);
        item.supplierPrices[q.quotationId] = {
          unitPrice: Number(detail.unitPrice),
          totalAmount: Number(detail.totalAmount),
          discountPercent: Number(detail.discountPercent || 0),
          qty: detail.qty,
          uom: detail.uom,
        };
      });
    });

    return Array.from(itemMap.values());
  };

  const items = buildItemMatrix();

  const getBestPrice = (supplierPrices) => {
    const prices = Object.values(supplierPrices)
      .map(p => p.unitPrice)
      .filter(p => p != null && !isNaN(p) && p > 0);
    return prices.length > 0 ? Math.min(...prices) : null;
  };

  const validTotals = localQuotations.map(q => q.grandTotal).filter(v => v != null && !isNaN(v) && v > 0);
  const lowestTotal = validTotals.length > 0 ? Math.min(...validTotals) : null;

  const handleSelectClick = (q) => {
    if (q.selected) {
      doSelect(q);
    } else {
      const fullQ = quotations.find(orig => orig.quotationId === q.quotationId) || q;
      setConfirmTarget(fullQ);
    }
  };
const doSelect = async (q) => {
  const alreadySelected = q.selected;
  const endpoint = alreadySelected
    ? `${API_BASE_URL}api/Quotation/Unselect/${q.quotationId}`
    : `${API_BASE_URL}api/Quotation/Select/${q.quotationId}`;

  // ✅ Optimistic update AGAD bago pa mag-fetch
  setLocalQuotations(prev =>
    prev.map(other => ({
      ...other,
      quotationDetails: other.quotationDetails || [],
      selected: other.quotationId === q.quotationId
        ? !alreadySelected
        : other.selected,
    }))
  );

  setSelecting(q.quotationId);
  try {
    const res = await fetch(endpoint, { method: "POST" });
    if (!res.ok) throw new Error("Failed");

    if (!alreadySelected) {
      setRatingTarget({ ...q, selected: true });
      setShowRatingModal(true);
    } else {
      if (onSelectSuccess) onSelectSuccess();
    }
  } catch (err) {
    console.error("Select failed:", err);
    // ✅ Ibalik kung nag-fail
    setLocalQuotations(prev =>
      prev.map(other => ({
        ...other,
        quotationDetails: other.quotationDetails || [],
        selected: other.quotationId === q.quotationId
          ? alreadySelected
          : other.selected,
      }))
    );
  } finally {
    setSelecting(null);
    setConfirmTarget(null);
  }
};


  // BAGO — standard PATCH sa quotation mismo:
  const handleSaveRating = async (rating) => {
    if (!ratingTarget) return;
    setSavingRating(true);
    try {
      // Option 1 — kung may dedicated rating endpoint:
      await fetch(`${API_BASE_URL}api/Quotation/UpdateRating/${ratingTarget.quotationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ supplierRating: rating }),
      });

      // Option 2 — kung walang dedicated, i-PUT yung buong quotation:
      // const res = await fetch(`${API_BASE_URL}api/Quotation/${ratingTarget.quotationId}`, {
      //   method: 'PUT',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ ...ratingTarget, supplierRating: rating }),
      // });

    } catch (err) {
      console.error('Rating save failed:', err);
    } finally {
      setSavingRating(false);
      setShowRatingModal(false);
      setRatingTarget(null);
      if (onSelectSuccess) onSelectSuccess();
    }
  };

  return (
    <>
      {showRatingModal && ratingTarget && (
        <RatingModal
          quotation={ratingTarget}
          saving={savingRating}
          onSave={handleSaveRating}
          onSkip={() => {
            setShowRatingModal(false);
            setRatingTarget(null);
            if (onSelectSuccess) onSelectSuccess();
          }}
        />
      )}
      {confirmTarget && (
        <ConfirmSelectModal
          quotation={confirmTarget}
          loading={selecting === confirmTarget.quotationId}
          onConfirm={() => doSelect(confirmTarget)}
          onCancel={() => setConfirmTarget(null)}
        />
      )}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
        {/* PR Header */}
        <div
          className="px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors flex items-center justify-between gap-4"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <ShoppingCart size={16} className="text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded">
                  PR #{prHeaderId}
                </span>
                <span className="text-xs text-slate-500">
                  {localQuotations.length} supplier{localQuotations.length !== 1 ? "s" : ""} quoted
                </span>
                <span className="text-xs text-slate-500">
                  {items.length} item{items.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                {localQuotations.map(q => (
                  <div key={q.quotationId} className="flex items-center gap-1.5">
                    {lowestTotal != null && q.grandTotal === lowestTotal && (
                      <Trophy size={11} className="text-amber-500 flex-shrink-0" />
                    )}
                    <span className={`text-xs font-semibold ${lowestTotal != null && q.grandTotal === lowestTotal ? "text-green-700" : "text-slate-600"}`}>
                      {q.supplierName}
                    </span>
                    <span className={`text-xs ${lowestTotal != null && q.grandTotal === lowestTotal ? "text-green-600 font-bold" : "text-slate-500"}`}>
                      {fmt(q.grandTotal)}
                    </span>
                    {q.selected && (
                      <span className="inline-flex items-center gap-0.5 bg-blue-100 text-blue-700 text-xs font-bold px-1.5 py-0.5 rounded-full border border-blue-200">
                        <CheckCircle size={9} /> Selected
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="text-right hidden sm:block">
              <p className="text-xs text-slate-400">Best total</p>
              <p className="text-sm font-black text-green-600">{fmt(lowestTotal)}</p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onOpenFeed) onOpenFeed(prHeaderId, { quotations: localQuotations });
              }}
              className="flex items-center gap-1.5 px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-600 border border-purple-200 rounded-xl text-xs font-bold transition-all"
              title="Activity Feed"
            >
              <MessageCircle size={14} /> Feed
            </button>
            <ChevronDown
              size={18}
              className={`text-slate-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
            />
          </div>
        </div>

        {/* Expanded Comparison Table */}
        {expanded && (
          <div className="border-t border-slate-100 overflow-x-auto">
            <table className="w-full text-xs min-w-[700px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left text-slate-400 font-semibold py-3 px-4 whitespace-nowrap sticky left-0 bg-slate-50 z-10 border-r border-slate-100">#</th>
                  <th className="text-left text-slate-400 font-semibold py-3 px-3 whitespace-nowrap sticky left-8 bg-slate-50 z-10 border-r border-slate-100">Item</th>
                  <th className="text-left text-slate-400 font-semibold py-3 px-3 whitespace-nowrap">Spec</th>
                  <th className="text-center text-slate-400 font-semibold py-3 px-3 whitespace-nowrap">PR Qty</th>
                  {localQuotations.map((q) => (
                    <th key={q.quotationId}
                      className={`py-3 px-3 min-w-[200px] transition-colors ${q.selected ? "bg-green-50 border-b-2 border-green-400" : ""}`}>
                      <div className="flex flex-col items-center gap-1.5">
                        {q.selected && (
                          <div className="flex items-center gap-1 bg-green-500 text-white text-[10px] font-black px-3 py-1 rounded-full w-full justify-center">
                            <CheckCircle size={10} /> This is Selected
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          {lowestTotal != null && q.grandTotal === lowestTotal && (
                            <Trophy size={10} className="text-amber-500" />
                          )}
                          <span className={`font-semibold text-xs ${q.selected ? "text-green-700" : lowestTotal != null && q.grandTotal === lowestTotal ? "text-green-700" : "text-slate-600"}`}>
                            {q.supplierName.length > 20 ? q.supplierName.slice(0, 20) + "…" : q.supplierName}
                          </span>
                        </div>
                        <StatusBadge status={q.status} />
                        {q.isAwarded && (
                          <span className="text-xs text-green-600 font-bold flex items-center gap-0.5">
                            <Award size={9} /> Awarded
                          </span>
                        )}
                        {/* SELECT / UNSELECT BUTTON */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleSelectClick(q); }}
                          disabled={selecting !== null}
                          className={`mt-1 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${q.selected
                            ? "bg-green-600 text-white border-green-700 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                            : "bg-white text-slate-600 border-slate-300 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300"
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {selecting === q.quotationId ? (
                            <><RefreshCw size={10} className="animate-spin" /> Saving...</>
                          ) : q.selected ? (
                            <><CheckCircle size={10} /> Selected</>
                          ) : (
                            <><Zap size={10} /> Select</>
                          )}
                        </button>
                        {/* DELETE BUTTON — SYSTEM ADMIN ONLY */}
                        {userRole === 'SYSTEM ADMIN' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onDelete(q.quotationId, q.quotationNo); }}
                            className="mt-1 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border bg-white text-red-500 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-400"
                          >
                            <Trash2 size={10} /> Delete
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="text-center text-slate-400 font-semibold py-3 px-3 whitespace-nowrap bg-amber-50">
                    Best Price
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={4 + localQuotations.length + 1} className="py-8 text-center text-slate-400">
                      No item data available
                    </td>
                  </tr>
                ) : items.map((item, idx) => {
                  const bestPrice = getBestPrice(item.supplierPrices);
                  return (
                    <tr key={item.key} className="border-b border-slate-50 hover:bg-slate-50/50 last:border-0">
                      <td className="py-2.5 px-4 text-slate-400 sticky left-0 bg-white border-r border-slate-100 z-10">{idx + 1}</td>
                      <td className="py-2.5 px-3 sticky left-8 bg-white border-r border-slate-100 z-10 max-w-[180px]">
                        <div className="font-semibold text-slate-700 truncate" title={item.itemDescription}>
                          {item.itemDescription}
                        </div>
                        {item.itemCode && item.itemCode !== "—" && (
                          <span className="mt-0.5 inline-block bg-blue-50 text-blue-600 text-xs px-1.5 py-0.5 rounded font-mono border border-blue-100">
                            {item.itemCode}
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-slate-400 max-w-[120px] truncate" title={item.specification}>
                        {item.specification || "—"}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <span className="font-semibold text-slate-600">
                          {item.originalQty ? Number(item.originalQty).toLocaleString() : "—"}
                        </span>
                        {item.originalUOM && <span className="text-slate-400 ml-1">{item.originalUOM}</span>}
                      </td>
                      {localQuotations.map((q) => {
                        const sp = item.supplierPrices[q.quotationId];
                        const isBest = sp && bestPrice != null && Number(sp.unitPrice) === bestPrice;
                        const isSelected = q.selected;
                        return (
                          <td key={q.quotationId}
                            className={`py-2.5 px-3 text-center transition-colors ${isSelected ? "bg-green-50/80" : isBest ? "bg-green-50" : ""}`}>
                            {sp && sp.unitPrice != null && !isNaN(sp.unitPrice) ? (
                              <div>
                                <div className={`font-bold ${isSelected || isBest ? "text-green-700" : "text-slate-700"}`}>
                                  {fmt(sp.unitPrice)}
                                  {isBest && (
                                    <span className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-500 text-white text-[9px] font-black">✓</span>
                                  )}
                                </div>
                                {sp.discountPercent > 0 && (
                                  <div className="text-amber-600 text-[10px]">-{sp.discountPercent}% disc</div>
                                )}
                                <div className="text-slate-400 text-[10px]">Total: {fmt(sp.totalAmount)}</div>
                              </div>
                            ) : (
                              <span className="text-slate-300 text-base">—</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="py-2.5 px-3 text-center bg-amber-50">
                        {bestPrice != null ? (
                          <span className="font-black text-amber-700">{fmt(bestPrice)}</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200 bg-slate-50">
                  <td colSpan={4} className="py-3 px-4 text-right text-slate-500 font-semibold text-xs">Grand Total</td>
                  {localQuotations.map((q) => (
                    <td key={q.quotationId}
                      className={`py-3 px-3 text-center transition-colors ${q.selected ? "bg-green-100" : lowestTotal != null && q.grandTotal === lowestTotal ? "bg-green-50" : ""}`}>
                      <div className={`font-black text-sm ${q.selected || (lowestTotal != null && q.grandTotal === lowestTotal) ? "text-green-700" : "text-slate-700"}`}>
                        {fmt(q.grandTotal)}
                      </div>
                      {lowestTotal != null && q.grandTotal === lowestTotal && !q.selected && (
                        <div className="text-green-500 text-[10px] font-semibold flex items-center justify-center gap-0.5 mt-0.5">
                          <Trophy size={9} /> Best
                        </div>
                      )}
                    </td>
                  ))}
                  <td className="py-3 px-3 text-center bg-amber-50">
                    <span className="font-black text-amber-700 text-sm">{fmt(lowestTotal)}</span>
                  </td>
                </tr>
                <tr className="border-t border-slate-100 bg-white">
                  <td colSpan={4} className="py-2 px-4 text-right text-slate-400 text-xs font-medium">Delivery</td>
                  {localQuotations.map((q) => (
                    <td key={q.quotationId} className={`py-2 px-3 text-center text-xs transition-colors ${q.selected ? "bg-green-50 text-green-700 font-semibold" : "text-slate-500"}`}>
                      {q.deliveryDays ? `${q.deliveryDays} days` : "—"}
                    </td>
                  ))}
                  <td className="bg-amber-50" />
                </tr>
                <tr className="border-t border-slate-100 bg-white">
                  <td colSpan={4} className="py-2 px-4 text-right text-slate-400 text-xs font-medium">Payment Terms</td>
                  {localQuotations.map((q) => (
                    <td key={q.quotationId} className={`py-2 px-3 text-center text-xs transition-colors ${q.selected ? "bg-green-50 text-green-700 font-semibold" : "text-slate-500"}`}>
                      {q.paymentTerms || "—"}
                    </td>
                  ))}
                  <td className="bg-amber-50" />
                </tr>
                <tr className="border-t border-slate-100 bg-white">
                  <td colSpan={4} className="py-2 px-4 text-right text-slate-400 text-xs font-medium">Rating</td>
                  {localQuotations.map((q) => (
                    <td key={q.quotationId} className={`py-2 px-3 transition-colors ${q.selected ? "bg-green-50" : ""}`}>
                      <div className="flex justify-center">
                        <StarDisplay value={q.supplierRating} size={11} />
                      </div>
                    </td>
                  ))}
                  <td className="bg-amber-50" />
                </tr>
                <tr className="border-t border-slate-100 bg-white">
                  <td colSpan={4} className="py-2 px-4 text-right text-slate-400 text-xs font-medium">Valid Until</td>
                  {localQuotations.map((q) => (
                    <td key={q.quotationId} className={`py-2 px-3 text-center text-xs transition-colors ${q.selected ? "bg-green-50 text-green-700 font-semibold" : "text-slate-500"}`}>
                      {fmtDate(q.validUntil)}
                    </td>
                  ))}
                  <td className="bg-amber-50" />
                </tr>
                <tr className="border-t-2 border-green-200 bg-green-50/30">
                  <td colSpan={4} className="py-3 px-4 text-right text-green-600 text-xs font-bold">✓ Selected</td>
                  {localQuotations.map((q) => (
                    <td key={q.quotationId} className={`py-3 px-3 text-center transition-colors ${q.selected ? "bg-green-100 border-t-2 border-green-400" : ""}`}>
                      {q.selected ? (
                        <span className="inline-flex items-center gap-1 bg-green-600 text-white text-xs font-black px-3 py-1.5 rounded-full shadow-sm shadow-green-200">
                          <CheckCircle size={11} /> This is Selected
                        </span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                  ))}
                  <td className="bg-amber-50" />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Quotation Card ──────────────────────────────────────────────
function QuotationCard({ q, isExpanded, onToggle, userRole, onDelete }) {
  return (
    <div className={`bg-white border rounded-2xl overflow-hidden transition-all duration-200 ${q.isAwarded ? "border-green-300" : "border-slate-200"}`}>
      <div className="px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors" onClick={onToggle}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className="font-mono text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded">
                {q.quotationNo}
              </span>
              <StatusBadge status={q.status} />
              {q.isAwarded && (
                <span className="inline-flex items-center gap-1 text-xs text-green-700 font-semibold bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                  <Award size={10} /> Awarded
                </span>
              )}
            </div>
            <p className="text-slate-800 font-bold text-base leading-tight truncate">{q.supplierName}</p>
            <p className="text-slate-400 text-xs mt-0.5">PR #{q.prHeaderId} &nbsp;•&nbsp; {fmtDate(q.createdAt)}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* DELETE BUTTON — SYSTEM ADMIN ONLY */}
            {userRole === 'SYSTEM ADMIN' && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(q.quotationId, q.quotationNo); }}
                className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all border border-transparent hover:border-red-200"
                title="Delete Quotation"
              >
                <Trash2 size={15} />
              </button>
            )}
            <div className="text-right">
              <p className="text-slate-400 text-xs">Grand Total</p>
              <p className="text-green-600 font-black text-lg leading-tight">{fmt(q.grandTotal)}</p>
            </div>
            <ChevronDown size={18} className={`text-slate-400 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3 items-center">
          {[
            `${q.deliveryDays ?? "—"} days delivery`,
            q.paymentTerms || "—",
            `Valid: ${fmtDate(q.validUntil)}`,
            `${q.quotationDetails?.length ?? 0} items`,
          ].map((label) => (
            <span key={label} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 border border-slate-200">{label}</span>
          ))}
          <div className="ml-auto"><StarDisplay value={q.supplierRating} size={12} /></div>
        </div>
      </div>
      {isExpanded && (
        <div className="border-t border-slate-200 bg-slate-50 px-5 py-4">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    {["#", "Code", "Description", "Spec", "Qty", "UOM", "Unit Price", "Disc %", "Total"].map((h) => (
                      <th key={h} className="text-left text-slate-400 text-xs font-semibold py-2.5 px-3 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(q.quotationDetails ?? []).map((item, idx) => (
                    <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50 last:border-0">
                      <td className="py-2 px-3 text-slate-400">{idx + 1}</td>
                      <td className="py-2 px-3">
                        <span className="bg-blue-50 text-blue-700 text-xs px-1.5 py-0.5 rounded font-mono border border-blue-100">
                          {item.itemCode || "—"}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-slate-700 font-medium max-w-[160px] truncate">{item.itemDescription}</td>
                      <td className="py-2 px-3 text-slate-400 max-w-[100px] truncate">{item.specification || "—"}</td>
                      <td className="py-2 px-3 text-slate-600 font-semibold">{Number(item.qty).toLocaleString()}</td>
                      <td className="py-2 px-3 text-slate-500">{item.uom}</td>
                      <td className="py-2 px-3 text-slate-700">{fmt(item.unitPrice)}</td>
                      <td className="py-2 px-3 text-slate-500">{item.discountPercent > 0 ? `${item.discountPercent}%` : "—"}</td>
                      <td className="py-2 px-3 text-green-600 font-bold">{fmt(item.totalAmount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200 bg-slate-50">
                    <td colSpan={8} className="py-3 px-3 text-right text-slate-500 font-semibold text-xs">Grand Total</td>
                    <td className="py-3 px-3 text-green-600 font-black text-sm">{fmt(q.grandTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Item Search Result ──────────────────────────────────────────
function ItemResultRow({ item, quotation }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-amber-50/50 transition-colors">
      <div className="w-8 h-8 bg-amber-50 border border-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
        <Package size={14} className="text-amber-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-slate-800 text-sm font-semibold truncate">{item.itemDescription}</p>
        <p className="text-slate-400 text-xs mt-0.5">
          <span className="font-mono bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded text-xs border border-blue-100 mr-2">
            {item.itemCode || "—"}
          </span>
          {item.specification || ""}
        </p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-green-600 font-bold text-sm">{fmt(item.unitPrice)}</p>
        <p className="text-slate-400 text-xs">{Number(item.qty).toLocaleString()} {item.uom}</p>
      </div>
      <div className="flex-shrink-0 text-right ml-2 hidden sm:block">
        <p className="text-slate-500 text-xs font-mono">{quotation.quotationNo}</p>
        <p className="text-slate-400 text-xs truncate max-w-[130px]">{quotation.supplierName}</p>
      </div>
      <div className="flex-shrink-0 hidden md:block">
        <StatusBadge status={quotation.status} />
      </div>
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────────
export default function QuotationView() {
  const [quotations, setQuotations] = useState([]);
  const [showFeedModal, setShowFeedModal] = useState(false);
  const [selectedFeedPR, setSelectedFeedPR] = useState(null);
  const [prDetailsMap, setPrDetailsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState("");
  const [itemSearch, setItemSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [sortBy, setSortBy] = useState("date_desc");
  const [viewMode, setViewMode] = useState("comparison");

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  // ── User Role ────────────────────────────────────────────────
  // AFTER — normalize to uppercase
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        const raw = user.positionId || user.role || user.Role || '';
        setUserRole(raw.toString().toUpperCase().trim());
      } catch { }
    }
  }, []);

  const [expandedDays, setExpandedDays] = useState({});
  const toggleDay = (dayKey) => setExpandedDays(p => ({ ...p, [dayKey]: !p[dayKey] }));

  const buildDateGroups = (filteredQuotes) => {
    const map = new Map();
    filteredQuotes.forEach(q => {
      const day = q.createdAt
        ? new Date(q.createdAt).toLocaleDateString("en-PH", { year: "numeric", month: "long", day: "2-digit", weekday: "long" })
        : "Unknown Date";
      const dayKey = q.createdAt ? new Date(q.createdAt).toISOString().split("T")[0] : "unknown";
      if (!map.has(dayKey)) {
        map.set(dayKey, { dayKey, dayLabel: day, quotations: [] });
      }
      map.get(dayKey).quotations.push(q);
    });
    return Array.from(map.values()).sort((a, b) => b.dayKey.localeCompare(a.dayKey));
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}api/Quotation`);
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      const quotes = (Array.isArray(data) ? data : []).map(q => ({
        ...q,
        quotationDetails: q.quotationDetails || q.items || [],
        selected: q.selected ?? q.Selected ?? q.isSelected ?? false,
      }));
      setQuotations(quotes);

      const prIds = [...new Set(quotes.map(q => q.prHeaderId).filter(Boolean))];
      const detailsMap = {};
      await Promise.all(
        prIds.map(async (prId) => {
          try {
            const r = await fetch(`${API_BASE_URL}api/PR_Details/PRHeader/${prId}`);
            if (r.ok) detailsMap[prId] = await r.json();
          } catch {
            detailsMap[prId] = [];
          }
        })
      );
      setPrDetailsMap(detailsMap);
    } catch (err) {
      setError(err.message || "Failed to load quotations.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Delete Handler ───────────────────────────────────────────
  const handleDeleteQuotation = async (quotationId, quotationNo) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete Quotation?',
      text: `This will permanently delete ${quotationNo}. This cannot be undone.`,
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await fetch(`${API_BASE_URL}api/Quotation/${quotationId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      await Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false });
      fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message });
    }
  };

  // ── Item search ──────────────────────────────────────────────
  const isItemMode = itemSearch.trim().length > 0;
  const itemResults = isItemMode
    ? quotations.flatMap((q) =>
      (q.quotationDetails ?? [])
        .filter((item) =>
          item.itemDescription?.toLowerCase().includes(itemSearch.toLowerCase()) ||
          item.itemCode?.toLowerCase().includes(itemSearch.toLowerCase()) ||
          item.specification?.toLowerCase().includes(itemSearch.toLowerCase())
        )
        .map((item) => ({ item, quotation: q }))
    )
    : [];

  const buildPRGroups = (filteredQuotes) => {
    const groupMap = new Map();
    filteredQuotes.forEach((q) => {
      const key = q.prHeaderId || "unassigned";
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          prHeaderId: key,
          quotations: [],
          prDetails: prDetailsMap[key] || [],
          latestDate: null,
        });
      }
      const g = groupMap.get(key);
      g.quotations.push(q);
      const d = new Date(q.createdAt);
      if (!g.latestDate || d > g.latestDate) g.latestDate = d;
    });
    return Array.from(groupMap.values());
  };

  const filtered = (() => {
    let r = [...quotations];
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((x) =>
        x.quotationNo?.toLowerCase().includes(q) ||
        x.supplierName?.toLowerCase().includes(q) ||
        String(x.prHeaderId).includes(q)
      );
    }
    if (statusFilter !== "") r = r.filter((x) => String(x.status) === statusFilter);
    if (dateRange !== "all") r = r.filter((x) => isInRange(x.createdAt, dateRange));
    r.sort((a, b) => {
      if (sortBy === "date_desc") return new Date(b.createdAt) - new Date(a.createdAt);
      if (sortBy === "date_asc") return new Date(a.createdAt) - new Date(b.createdAt);
      if (sortBy === "total_asc") return a.grandTotal - b.grandTotal;
      if (sortBy === "total_desc") return b.grandTotal - a.grandTotal;
      return 0;
    });
    return r;
  })();

  const prGroups = buildPRGroups(filtered);

  const ranged = dateRange === "all" ? quotations : quotations.filter((q) => isInRange(q.createdAt, dateRange));
  const stats = {
    total: ranged.length,
    submitted: ranged.filter((q) => q.status === 1).length,
    awarded: ranged.filter((q) => q.status === 2).length,
    value: ranged.reduce((s, q) => s + (q.grandTotal || 0), 0),
  };

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const groupTotalPages = Math.ceil(prGroups.length / PAGE_SIZE);
  const paginatedGroups = prGroups.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggle = (id) => setExpandedId((p) => (p === id ? null : id));

  const DATE_TABS = [
    { key: "all", label: "All time" },
    { key: "today", label: "Today" },
    { key: "week", label: "This week" },
    { key: "month", label: "This month" },
  ];

  // Table view col count — extra col if SYSTEM ADMIN
  const tableColCount = userRole === 'SYSTEM ADMIN' ? 10 : 9;

  return (
    <div className="max-w-8xl mx-auto px-6 py-8">

      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileText size={22} className="text-blue-600" /> Quotation Records
          </h1>
          <p className="text-slate-400 text-sm mt-0.5">Compare supplier quotations and find the best deal per PR</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-all"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Date Range Tabs */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <Calendar size={14} className="text-slate-400" />
        {DATE_TABS.map(({ key, label }) => (
          <button key={key}
            onClick={() => { setDateRange(key); setPage(1); }}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${dateRange === key
              ? "bg-blue-600 text-white shadow-md shadow-blue-200"
              : "bg-white border border-slate-200 text-slate-500 hover:text-blue-600 hover:border-blue-200"
              }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Quotations", value: stats.total, color: "text-blue-600", bg: "bg-blue-50" },
          { label: "Submitted", value: stats.submitted, color: "text-slate-700", bg: "bg-slate-100" },
          { label: "Awarded", value: stats.awarded, color: "text-green-600", bg: "bg-green-50" },
          { label: "Total Value", value: fmt(stats.value), color: "text-amber-600", bg: "bg-amber-50" },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className={`${bg} rounded-xl px-5 py-4`}>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
            <p className={`text-2xl font-black mt-1 ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
          <input type="text" value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search quote no., supplier, PR..."
            className="w-full pl-9 pr-9 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X size={14} />
            </button>
          )}
        </div>

        <div className="relative min-w-[200px]">
          <Package className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-500" size={15} />
          <input type="text" value={itemSearch}
            onChange={(e) => setItemSearch(e.target.value)}
            placeholder="Search items / item code..."
            className="w-full pl-9 pr-9 py-2.5 border border-amber-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-amber-50/40"
          />
          {itemSearch && (
            <button onClick={() => setItemSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X size={14} />
            </button>
          )}
        </div>

        <select value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
        >
          <option value="">All Status</option>
          <option value="0">Draft</option>
          <option value="1">Submitted</option>
          <option value="2">Awarded</option>
          <option value="3">Rejected</option>
        </select>

        <select value={sortBy}
          onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
          className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
        >
          <option value="date_desc">Newest first</option>
          <option value="date_asc">Oldest first</option>
          <option value="total_asc">Lowest total</option>
          <option value="total_desc">Highest total</option>
        </select>

        <div className="flex border border-slate-200 rounded-xl overflow-hidden ml-auto">
          <button onClick={() => { setViewMode("comparison"); setPage(1); }}
            title="Compare by PR"
            className={`px-3 py-2.5 text-sm flex items-center gap-1.5 transition-colors ${viewMode === "comparison" ? "bg-blue-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>
            <BarChart2 size={15} /> Compare
          </button>
          <button onClick={() => { setViewMode("card"); setPage(1); }}
            title="Card view"
            className={`px-3 py-2.5 text-sm flex items-center gap-1.5 transition-colors border-l border-slate-200 ${viewMode === "card" ? "bg-blue-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>
            <LayoutGrid size={15} /> Cards
          </button>
          <button onClick={() => { setViewMode("table"); setPage(1); }}
            title="Table view"
            className={`px-3 py-2.5 text-sm flex items-center gap-1.5 transition-colors border-l border-slate-200 ${viewMode === "table" ? "bg-blue-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>
            <List size={15} /> Table
          </button>
        </div>
      </div>

      {/* Legend */}
      {viewMode === "comparison" && !isItemMode && (
        <div className="flex items-center gap-4 mb-4 px-1 text-xs text-slate-500 flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded bg-green-50 border border-green-200 flex items-center justify-center">
              <span className="w-2 h-2 rounded-full bg-green-500" />
            </span>
            Lowest price for item
          </div>
          <div className="flex items-center gap-1.5">
            <Trophy size={12} className="text-amber-500" />
            Best grand total supplier
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded bg-amber-50 border border-amber-200" />
            Best price column
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm mb-5 flex items-center gap-2">
          <XCircle size={15} /> {error}
        </div>
      )}

      {/* Item Search Mode */}
      {isItemMode && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Package size={15} className="text-amber-500" />
            <p className="text-sm font-semibold text-slate-700">
              Items matching <span className="text-amber-600">"{itemSearch}"</span>
              <span className="text-slate-400 font-normal ml-2">— {itemResults.length} result{itemResults.length !== 1 ? "s" : ""}</span>
            </p>
          </div>
          <div className="bg-white border border-amber-200 rounded-2xl overflow-hidden">
            {itemResults.length === 0 ? (
              <div className="py-12 text-center text-slate-400">
                <Package size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No items found for "{itemSearch}"</p>
              </div>
            ) : itemResults.map(({ item, quotation }, idx) => (
              <ItemResultRow key={idx} item={item} quotation={quotation} />
            ))}
          </div>
        </div>
      )}

      {/* Main List */}
      {!isItemMode && (
        <>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-3">
              <RefreshCw size={24} className="animate-spin" />
              <p className="text-sm">Loading quotations...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl py-16 text-center">
              <FileText size={40} className="mx-auto mb-3 text-slate-200" />
              <p className="text-slate-500 font-semibold">No quotations found</p>
              <p className="text-slate-400 text-sm mt-1">Try adjusting your search or filters.</p>
            </div>
          ) : viewMode === "comparison" ? (
            <div className="space-y-3">
              {buildDateGroups(filtered).map(({ dayKey, dayLabel, quotations: dayQuotes }) => {
                const isOpen = expandedDays[dayKey];
                const dayGroups = buildPRGroups(dayQuotes);
                const todayKey = new Date().toISOString().split("T")[0];
                const isToday = dayKey === todayKey;
                return (
                  <div key={dayKey} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    {/* Folder Header */}
                    <button
                      onClick={() => toggleDay(dayKey)}
                      className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isToday ? 'bg-blue-600' : 'bg-slate-100'}`}>
                          <Calendar size={16} className={isToday ? 'text-white' : 'text-slate-500'} />
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-800 text-sm">{dayLabel}</span>
                            {isToday && (
                              <span className="bg-blue-600 text-white text-xs font-black px-2 py-0.5 rounded-full">TODAY</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-xs text-slate-400">{dayQuotes.length} quotation{dayQuotes.length !== 1 ? 's' : ''}</span>
                            <span className="text-xs text-slate-400">•</span>
                            <span className="text-xs text-slate-400">{dayGroups.length} PR{dayGroups.length !== 1 ? 's' : ''}</span>
                            <span className="text-xs text-slate-400">•</span>
                            <span className="text-xs font-semibold text-green-600">
                              {fmt(dayQuotes.reduce((s, q) => s + (q.grandTotal || 0), 0))} total
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {/* Selected badge if may selected sa araw na to */}
                        {dayQuotes.some(q => q.selected) && (
                          <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 border border-green-200 text-xs font-bold px-2 py-0.5 rounded-full">
                            <CheckCircle size={10} /> Has Selected
                          </span>
                        )}
                        <ChevronDown size={18} className={`text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </button>

                    {/* Folder Content */}
                    {isOpen && (
                      <div className="border-t border-slate-100 p-4 space-y-4 bg-slate-50/50">
                        {dayGroups.map((group) => (
                          <BestQuotationView
                            key={group.prHeaderId}
                            prGroup={group}
                            onSelectSuccess={fetchData}
                            userRole={userRole}
                            onDelete={handleDeleteQuotation}
                            onOpenFeed={(prId, grp) => {
                              const selectedQ = (grp?.quotations || []).find(q => q.selected)
                                || (grp?.quotations || [])[0] || {};
                              setSelectedFeedPR({
                                id: selectedQ.quotationId,
                                quotationId: selectedQ.quotationId,
                                prNo: `PR #${prId}`,
                                supplierName: selectedQ.supplierName || `${(grp?.quotations || []).length} suppliers`,
                                quotationNo: selectedQ.quotationNo || null,
                                grandTotal: selectedQ.grandTotal ?? null,
                                deliveryDays: selectedQ.deliveryDays || null,
                                paymentTerms: selectedQ.paymentTerms || null,
                                validUntil: selectedQ.validUntil || null,
                                createdAt: selectedQ.createdAt || null,
                                supplierRating: selectedQ.supplierRating ?? null,
                                quotStatus: selectedQ.status ?? null,
                                isAwarded: selectedQ.isAwarded || false,
                                itemCount: selectedQ.quotationDetails?.length ?? null,
                                remarks: selectedQ.remarks || null,
                              });
                              setShowFeedModal(true);
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : viewMode === "card" ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {paginated.map((q) => (
                <QuotationCard
                  key={q.quotationId}
                  q={q}
                  isExpanded={expandedId === q.quotationId}
                  onToggle={() => toggle(q.quotationId)}
                  userRole={userRole}
                  onDelete={handleDeleteQuotation}
                />
              ))}
            </div>
          ) : (
            // ── TABLE VIEW ──
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="w-10 py-3 px-4"></th>
                    {["Quote No.", "PR", "Supplier", "Grand Total", "Delivery", "Rating", "Status", "Date"].map((h) => (
                      <th key={h} className="text-left py-3 px-4 text-slate-500 text-xs font-semibold uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                    {/* ACTION COLUMN — SYSTEM ADMIN ONLY */}
                    {userRole === 'SYSTEM ADMIN' && (
                      <th className="py-3 px-4 text-slate-500 text-xs font-semibold uppercase tracking-wider text-center">Action</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((q) => (
                    <>
                      <tr key={q.quotationId}
                        onClick={() => toggle(q.quotationId)}
                        className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${expandedId === q.quotationId ? "bg-slate-50" : ""}`}
                      >
                        <td className="py-3.5 px-4">
                          <ChevronRight size={15} className={`text-slate-400 transition-transform duration-200 ${expandedId === q.quotationId ? "rotate-90" : ""}`} />
                        </td>
                        <td className="py-3.5 px-4">
                          <span className="font-mono text-blue-700 text-xs font-semibold bg-blue-50 border border-blue-100 px-2 py-0.5 rounded">{q.quotationNo}</span>
                        </td>
                        <td className="py-3.5 px-4 text-slate-500 text-xs font-mono">#{q.prHeaderId}</td>
                        <td className="py-3.5 px-4">
                          <p className="text-slate-800 font-semibold text-sm">{q.supplierName}</p>
                          <p className="text-slate-400 text-xs mt-0.5">{q.supplierEmail || q.supplierContact || ""}</p>
                        </td>
                        <td className="py-3.5 px-4 text-green-600 font-bold">{fmt(q.grandTotal)}</td>
                        <td className="py-3.5 px-4 text-slate-600 text-sm">{q.deliveryDays ? `${q.deliveryDays} days` : "—"}</td>
                        <td className="py-3.5 px-4"><StarDisplay value={q.supplierRating} /></td>
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-1.5">
                            <StatusBadge status={q.status} />
                            {q.isAwarded && <span className="text-xs text-green-600 font-semibold flex items-center gap-0.5"><Award size={11} /> Awarded</span>}
                          </div>
                        </td>
                        <td className="py-3.5 px-4 text-slate-500 text-xs">{fmtDate(q.createdAt)}</td>
                        {/* DELETE — TABLE VIEW */}
                        {userRole === 'SYSTEM ADMIN' && (
                          <td className="py-3.5 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleDeleteQuotation(q.quotationId, q.quotationNo)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-xs font-bold transition-all"
                            >
                              <Trash2 size={12} /> Delete
                            </button>
                          </td>
                        )}
                      </tr>
                      {expandedId === q.quotationId && (
                        <tr key={`exp-${q.quotationId}`} className="border-b border-slate-200">
                          <td colSpan={tableColCount} className="p-0">
                            <div className="bg-slate-50 border-t border-slate-200 px-6 py-4">
                              <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="bg-slate-50 border-b border-slate-100">
                                      {["#", "Code", "Description", "Spec", "Qty", "UOM", "Unit Price", "Disc %", "Total"].map(h => (
                                        <th key={h} className="text-left text-slate-400 font-semibold py-2.5 px-3 whitespace-nowrap">{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(q.quotationDetails ?? []).map((item, idx) => (
                                      <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50 last:border-0">
                                        <td className="py-2 px-3 text-slate-400">{idx + 1}</td>
                                        <td className="py-2 px-3"><span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-mono border border-blue-100">{item.itemCode || "—"}</span></td>
                                        <td className="py-2 px-3 text-slate-700 font-medium max-w-[160px] truncate">{item.itemDescription}</td>
                                        <td className="py-2 px-3 text-slate-400 max-w-[100px] truncate">{item.specification || "—"}</td>
                                        <td className="py-2 px-3 text-slate-600 font-semibold">{Number(item.qty).toLocaleString()}</td>
                                        <td className="py-2 px-3 text-slate-500">{item.uom}</td>
                                        <td className="py-2 px-3 text-slate-700">{fmt(item.unitPrice)}</td>
                                        <td className="py-2 px-3 text-slate-500">{item.discountPercent > 0 ? `${item.discountPercent}%` : "—"}</td>
                                        <td className="py-2 px-3 text-green-600 font-bold">{fmt(item.totalAmount)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr className="border-t-2 border-slate-200 bg-slate-50">
                                      <td colSpan={8} className="py-3 px-3 text-right text-slate-500 font-semibold">Grand Total</td>
                                      <td className="py-3 px-3 text-green-600 font-black text-sm">{fmt(q.grandTotal)}</td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Activity Feed Modal */}
          {showFeedModal && selectedFeedPR && (
            <ActivityFeedModal
              request={selectedFeedPR}
              onClose={() => {
                setShowFeedModal(false);
                setSelectedFeedPR(null);
              }}
            />
          )}

          {/* Pagination */}
          {(viewMode !== "comparison" && totalPages > 1) && (
            <div className="flex items-center justify-between mt-5 text-sm text-slate-500 flex-wrap gap-3">
              <span>
                {viewMode === "comparison"
                  ? `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered.length)} of ${filtered.length} PRs`
                  : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered.length)} of ${filtered.length}`
                }
              </span>
              <div className="flex gap-1 flex-wrap">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
                  Prev
                </button>
                {Array.from({ length: (viewMode === "comparison" ? groupTotalPages : totalPages) }, (_, i) => i + 1)
                  .slice(Math.max(0, page - 3), Math.min((viewMode === "comparison" ? groupTotalPages : totalPages), page + 2))
                  .map((p) => (
                    <button key={p} onClick={() => setPage(p)}
                      className={`px-3 py-1.5 rounded-lg text-xs border ${p === page ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 hover:bg-slate-50"}`}>
                      {p}
                    </button>
                  ))}
                <button onClick={() => setPage((p) => Math.min((viewMode === "comparison" ? groupTotalPages : totalPages), p + 1))}
                  disabled={page === (viewMode === "comparison" ? groupTotalPages : totalPages)}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
function RatingModal({ quotation, onSave, onSkip, saving }) {
  const [rating, setRating] = useState(quotation?.supplierRating || 0);
  const labels = ["", "Poor", "Fair", "Good", "Very Good", "Excellent"];
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm mx-4 overflow-hidden">
        <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-t-2xl">
          <h2 className="text-white font-bold text-lg flex items-center gap-2">
            <Star size={18} className="fill-white text-white" /> Rate this Supplier
          </h2>
          <p className="text-amber-100 text-sm mt-0.5">{quotation?.supplierName}</p>
        </div>
        <div className="px-6 py-6 text-center">
          <p className="text-slate-500 text-sm mb-4">How would you rate this supplier's quotation?</p>
          <div className="flex justify-center gap-2 mb-3">
            {[1, 2, 3, 4, 5].map(s => (
              <button key={s} onClick={() => setRating(s)}
                className="transition-transform hover:scale-125">
                <Star size={36} className={s <= rating ? "fill-amber-400 text-amber-400" : "fill-slate-100 text-slate-300"} />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="text-amber-600 font-bold text-sm mb-4">{labels[rating]}</p>
          )}
          <div className="flex gap-3 mt-4">

            <button onClick={() => onSave(rating)} disabled={saving || rating === 0}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-amber-400 to-yellow-500 text-white rounded-xl text-sm font-bold hover:from-amber-500 hover:to-yellow-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              {saving ? <><RefreshCw size={13} className="animate-spin" /> Saving...</> : <><CheckCircle size={13} /> Save Rating</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}